import axios from 'axios';
import qs from 'qs';
import { isEmpty } from 'radash';
import { createInfiniteQuery, createMutation, createQuery } from 'react-query-kit';

import { HTTP_HEADERS } from '@/constants';
import { consumeWithVirusScanRetry, deleteUpload, stageFile } from '@/services/stagedUpload';
import { filterEmptyParams, relativeUrlResolver } from '@/services/utils';

import type { AssetApiRecord, AssetsListResponse } from './assets.types';

const ASSETS_API_BASE = '/api/v2/assets/';
const ASSETS_LIST_URL = relativeUrlResolver(ASSETS_API_BASE);
const ASSET_CARD_LAYOUT = 'asset_card';
const ASSET_DETAIL_LAYOUT = 'asset_detail';

interface AssetsQueryVariables {
    count: number;
    keyword?: string;
    category?: string;
    layout?: string;
    /** Sort field(s) and matching `asc` / `desc` direction(s), see `Page`. */
    sort?: string[];
    direction?: string[];
    /** Repeatable `attribute=value` pairs, see `AssetQuery#filters`. */
    filter?: string[];
    /**
     * Picker context: item category's `assetLinkPickerFilters[<attribute>]` payload
     * shape (see `mdm/src/main/java/com/lansa/lax/pim/AssetQuery.java`). Together
     * these constrain suggestions to assets whose category + type-attribute value
     * satisfy the mapping declared for the target `Asset()` attribute.
     */
    pickerContextAssetCategory?: string;
    pickerContextTypeAttribute?: string;
    pickerContextAllowedTypes?: string[];
}

interface AssetQueryVariables {
    primaryKey: string;
    layout?: string;
}

interface AssetLinkedItemsQueryVariables {
    primaryKey: string;
}

interface AssetLinkedItemsResponse {
    itemPrimaryKeys: string[];
}

const fetchAssetsPage = async (variables: AssetsQueryVariables, cursor?: string): Promise<AssetsListResponse> => {
    const response = await axios.get(ASSETS_LIST_URL, {
        params: {
            ...variables,
            layout: variables.layout ?? ASSET_CARD_LAYOUT,
        },
        headers: {
            [HTTP_HEADERS.X_CURSOR]: cursor,
        },
        // `arrayFormat: 'repeat'` — JAX-RS `List<T>` @QueryParam expects
        // repeated pairs (`k=a&k=b`), not qs's default indexed shape.
        paramsSerializer: (params) => qs.stringify(filterEmptyParams(params), { arrayFormat: 'repeat' }),
    });

    const foundHeader = response.headers[HTTP_HEADERS.X_FOUND];
    const found = foundHeader != null ? Number(foundHeader) : (response.data?.length ?? 0);

    return {
        data: response.data ?? [],
        cursor: response.headers[HTTP_HEADERS.X_CURSOR] ?? undefined,
        found: Number.isFinite(found) ? found : response.data?.length ?? 0,
    };
};

/** Every asset matching the query, page by page; backs "select all matching" bulk actions. */
const fetchAllMatchingAssets = async (variables: AssetsQueryVariables): Promise<AssetApiRecord[]> => {
    const records: AssetApiRecord[] = [];
    let cursor: string | undefined;
    do {
        const page = await fetchAssetsPage(variables, cursor);
        records.push(...page.data);
        cursor = isEmpty(page.data) ? undefined : page.cursor;
    } while (cursor);
    return records;
};

const useAssets = createInfiniteQuery<
    AssetsListResponse,
    AssetsQueryVariables,
    Error,
    string | undefined
>({
    queryKey: ['assets', 'library'],
    fetcher: (variables, { pageParam }) => fetchAssetsPage(variables, pageParam),
    getNextPageParam: ({ data, cursor }) =>
        isEmpty(data) || isEmpty(cursor) ? null : cursor,
    initialPageParam: undefined,
});

const useAsset = createQuery<AssetApiRecord, AssetQueryVariables, Error>({
    queryKey: ['assets', 'detail'],
    fetcher: async ({ primaryKey, layout }) => {
        const response = await axios.get(
            relativeUrlResolver(`${ASSETS_API_BASE}${encodeURIComponent(primaryKey)}`),
            {
                params: {
                    layout: layout ?? ASSET_DETAIL_LAYOUT,
                },
                paramsSerializer: (params) => qs.stringify(filterEmptyParams(params)),
            },
        );
        return response.data;
    },
});

const useAssetLinkedItems = createQuery<AssetLinkedItemsResponse, AssetLinkedItemsQueryVariables, Error>({
    queryKey: ['assets', 'linked-items'],
    fetcher: async ({ primaryKey }) => {
        const response = await axios.get(
            relativeUrlResolver(`${ASSETS_API_BASE}${encodeURIComponent(primaryKey)}/items`),
        );
        return response.data;
    },
});

interface ReplaceAssetVariables {
    primaryKey: string;
    file: File;
    onProgress?: (percent: number) => void;
}

/**
 * Stages the file via the upload API, then runs the feature call that consumes the staged upload.
 * The staged row is deleted again when staging or the consume call fails (a consumed upload is
 * released by the server), so no orphaned uploads are left behind.
 */
const stageAndConsume = async <T>(
    file: File,
    onProgress: ((percent: number) => void) | undefined,
    consume: (uploadId: number) => Promise<T>,
): Promise<T> => {
    let createdUploadId: number | undefined;
    try {
        const { uploadId } = await stageFile({
            file,
            onProgress,
            onCreated: (id) => {
                createdUploadId = id;
            },
        });
        return await consumeWithVirusScanRetry(() => consume(uploadId));
    } catch (error) {
        if (createdUploadId !== undefined) {
            await deleteUpload(createdUploadId).catch(() => undefined);
        }
        throw error;
    }
};

/** Swaps the binary only; metadata, labels and item links are retained server-side. */
const useReplaceAsset = createMutation<AssetApiRecord, ReplaceAssetVariables, Error>({
    mutationFn: ({ primaryKey, file, onProgress }) =>
        stageAndConsume(file, onProgress, async (uploadId) => {
            const response = await axios.post(
                relativeUrlResolver(`${ASSETS_API_BASE}${encodeURIComponent(primaryKey)}/replace`),
                { uploadId },
            );
            return response.data;
        }),
});

interface UpdateAssetVariables {
    primaryKey: string;
    category?: string;
    values: Record<string, unknown>;
}

/** Merges the given business values into the stored asset; unknown keys are left untouched. */
const updateAsset = async ({ primaryKey, category, values }: UpdateAssetVariables): Promise<AssetApiRecord> => {
    const response = await axios.post(
        ASSETS_LIST_URL,
        { ...values, primaryKey__: primaryKey, category__: category },
        { params: { merge: true, updateOnly: true } },
    );
    return response.data;
};

const useUpdateAsset = createMutation<AssetApiRecord, UpdateAssetVariables, Error>({
    mutationFn: updateAsset,
});

const deleteAsset = async ({ primaryKey }: { primaryKey: string }) => {
    await axios.delete(relativeUrlResolver(`${ASSETS_API_BASE}${encodeURIComponent(primaryKey)}`));
};

const useDeleteAsset = createMutation<void, { primaryKey: string }, Error>({
    mutationFn: deleteAsset,
});

interface CreateAssetVariables {
    file: File;
    category?: string;
    onProgress?: (percent: number) => void;
}

/** Stages the binary, then stores a new asset from the staged upload; 409 when the content already exists. */
const createAssetWithFile = ({ file, category, onProgress }: CreateAssetVariables): Promise<AssetApiRecord> =>
    stageAndConsume(file, onProgress, async (uploadId) => {
        const response = await axios.post(
            ASSETS_LIST_URL,
            category ? { category__: category } : {},
            { params: { uploadId } },
        );
        return response.data;
    });

/** Asset category names of the active data model, in model order. */
const useAssetCategories = createQuery<string[], void, Error>({
    queryKey: ['assets', 'categories'],
    fetcher: async () => {
        const response = await axios.get(relativeUrlResolver(`${ASSETS_API_BASE}categories`));
        return Object.keys(response.data ?? {});
    },
});

export type { AssetsQueryVariables };
export {
    ASSET_CARD_LAYOUT,
    ASSET_DETAIL_LAYOUT,
    createAssetWithFile,
    deleteAsset,
    fetchAllMatchingAssets,
    updateAsset,
    useAsset,
    useAssetCategories,
    useAssetLinkedItems,
    useAssets,
    useDeleteAsset,
    useReplaceAsset,
    useUpdateAsset,
};
